import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { AutoCompleteCombobox } from '@/components/forms/AutoCompleteCombobox'
import { DatePicker } from '@/components/forms/DatePicker'
import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { Modal } from '@/components/ui/Modal'
import { updateRepairOrder } from '@/services/repairOrders.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast, verifyCurrencyInputValue } from '@/utils/helpers'

const RepairOrderModal = ({
  isOpen,
  setIsOpen,
  getUpdatedRepairOrder,
  repairOrder,
  closeModal,
  allAdvisors,
  dealer,
  setIsDeleteModalOpen,
  setIsReturnToIntakeModalOpen,
}) => {
  // Context
  const { isWpcAdmin, isWpcManager, isWpcExecutive } = useContext(UserStoreContext)
  const { createdAt } = repairOrder

  // RO Intake dealers get intake-specific behavior in this modal: the RO number is
  // locked (captured at intake, canonical once on the OL, still editable from RO
  // Intake before it's on the list), and the destructive footer action is "Return to
  // Intake" instead of "Delete RO" — deleting isn't the right operation for an intake
  // order, returning it to intake is.
  const isRoIntakeDealer = Boolean(dealer?.isRoIntakeEnabled)
  const isRoNumberLocked = isRoIntakeDealer
  // "Return to Intake" only applies while the RO is actively On Outstanding List. Once it's
  // Closed/Submitted/Paid/Resolved, returning it to intake no longer makes sense, so intake
  // dealers fall back to the normal Delete action.
  const isOnOutstandingList = repairOrder?.status === 'On Outstanding List'
  const canReturnToIntake = isRoIntakeDealer && isOnOutstandingList

  // State
  const [loading, setLoading] = useState(false)
  const [advisorQuery, setAdvisorQuery] = useState('')
  const [filteredAdvisors, setFilteredAdvisors] = useState([])
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false)

  const handleSuccess = (message) => {
    toast(message, 'success')
    closeModal()
    getUpdatedRepairOrder()
  }
  const handleError = (message) => toast(message, 'error')

  useEffect(() => {
    if (isOpen) {
      const creationDate = new Date(createdAt)
      const hoursSinceCreation = Math.abs(new Date() - creationDate) / (1000 * 60 * 60)
      // Enable the Delete button for Managers and Executives always, and for Administrators if within 72 hours
      setIsDeleteEnabled(
        isWpcManager || isWpcExecutive || (isWpcAdmin && hoursSinceCreation <= 72),
      )
    }
  }, [isOpen, isWpcAdmin, isWpcManager, isWpcExecutive, createdAt])

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    reset,
    control,
    setError,
  } = useForm({
    defaultValues: {
      roNumber: '',
      advisor: '',
      isAgesOutOverridden: false,
      timeClockTriggerDate: '',
      isOpen: false,
      agesOut: '',
    },
  })

  useEffect(() => {
    if (repairOrder) {
      reset({
        ...repairOrder,
        timeClockTriggerDate:
          repairOrder.timeClockTriggerDate &&
          dayjs(repairOrder.timeClockTriggerDate).format('MM/DD/YYYY'),
        agesOut: dayjs(repairOrder.agesOut).format('MM/DD/YYYY'),
      })
    }
  }, [repairOrder])

  useEffect(() => {
    if (advisorQuery.length >= 2) {
      const lowerCaseQuery = advisorQuery.toLowerCase()

      setFilteredAdvisors(
        allAdvisors.filter((person) => person.toLowerCase().includes(lowerCaseQuery)),
      )
    } else {
      setFilteredAdvisors([])
    }
  }, [advisorQuery, allAdvisors])

  /**
   * Handles updating the existing repair order.
   * @param {object} data
   * @returns
   */
  const onSubmit = async (data) => {
    const payload = {
      ...data,
      timeClockTriggerDate:
        data.timeClockTriggerDate && dayjs(data.timeClockTriggerDate).format('YYYY-MM-DD'),
      agesOut: dayjs(data.agesOut).format('YYYY-MM-DD'),
    }

    // The RO number is locked for RO Intake dealers on the OL, so never send it — a
    // disabled input may also be dropped from react-hook-form's data entirely. For
    // everyone else, normalize casing.
    if (isRoNumberLocked) {
      delete payload.roNumber
    } else {
      payload.roNumber = data.roNumber.toUpperCase()
    }

    // If we are marking `isAgesOutOverridden` as false and we already have a
    // value for `agesOut`, we need to clear it out
    if (!data.isAgesOutOverridden && data.agesOut) {
      payload.agesOut = null
    }

    await updateRepairOrder(dealer.id, repairOrder.id, payload, setLoading, handleError, () => {
      handleSuccess('Repair Order updated successfully.')
    })
  }

  const onDelete = async () => {
    setIsOpen(false)
    setIsDeleteModalOpen(true)
  }

  const onReturnToIntake = () => {
    setIsOpen(false)
    setIsReturnToIntakeModalOpen(true)
  }

  const renderRepairOrderDateField = () => {
    const isAgesOutOverridden = watch('isAgesOutOverridden')
    const trigger = dealer?.manufacturer.timeClockTrigger

    // Conditional message for RO close date
    if (watch('isOpen') && !isAgesOutOverridden && trigger === 'RO close date') {
      return (
        <div className="mb-[52px]">
          <span className="text-center text-xs font-light italic leading-5 tracking-[1.25px]">
            For this manufacturer, all open Repair Orders will age out 30 days after the date added
            to the Outstanding List.
          </span>
        </div>
      )
    }

    if (isAgesOutOverridden) {
      // If agesOut is overridden, show the agesOut field
      return (
        <Controller
          name="agesOut"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              disabled={loading}
              dataTestId="agesOut"
              error={
                errors.agesOut
                  ? { message: errors.agesOut.message || 'This field is required' }
                  : null
              }
              label="Age Out Date Override (MM/DD/YYYY)"
              onChange={onChange}
              show={showDatePicker}
              setShow={setShowDatePicker}
              value={value}
            />
          )}
          rules={{
            required: true,
          }}
        />
      )
    }

    return (
      <Controller
        name="timeClockTriggerDate"
        control={control}
        render={({ field: { onChange, value } }) => (
          <DatePicker
            disabled={loading}
            dataTestId="timeClockTriggerDate"
            error={
              errors.timeClockTriggerDate
                ? { message: errors.timeClockTriggerDate.message || 'This field is required' }
                : null
            }
            label={`${trigger} (MM/DD/YYYY)`}
            onChange={onChange}
            show={showDatePicker}
            setShow={setShowDatePicker}
            value={value}
          />
        )}
        rules={{
          required: true,
        }}
      />
    )
  }

  return (
    <Modal
      open={isOpen}
      title="Edit Repair Order"
      setOpen={setIsOpen}
      loading={loading}
      onClose={() => closeModal()}
      content={
        <form onSubmit={handleSubmit(onSubmit)} className="my-8 flex flex-col gap-4">
          <TextInput
            data-testid="roNumber"
            error={errors.roNumber && 'This field is required'}
            label="REPAIR ORDER #"
            inputStyles="uppercase"
            id="roNumber"
            name="roNumber"
            placeholder=""
            required
            fullWidth
            disabled={isRoNumberLocked}
            infoTooltip={
              isRoNumberLocked
                ? 'For RO Intake dealers, the repair order number is set during intake and cannot be edited from the Outstanding List.'
                : null
            }
            {...register('roNumber', { required: true })}
          />

          <Controller
            name="advisor"
            control={control}
            rules={{ required: 'This field is required' }}
            render={({ field: { onChange, value } }) => (
              <AutoCompleteCombobox
                id="advisor-id"
                dataTestId="advisor"
                label="ADV #"
                value={value}
                inputClassName="font-medium text-black"
                onComboboxChange={(fieldValue) => {
                  setValue('advisor', fieldValue)
                  clearErrors('advisor')
                }}
                options={filteredAdvisors}
                error={errors.advisor?.message}
                onComboboxInputChange={(event) => {
                  const inputValue = event.target.value
                  setAdvisorQuery(inputValue)
                  setValue('advisor', inputValue, { shouldValidate: true })
                  clearErrors('advisor')
                }}
                onChange={onChange}
              />
            )}
          />

          {/* Open Status */}
          <Controller
            name="isOpen"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Toggle label="Open RO?" checked={value} onChange={onChange} />
            )}
          />

          {watch('isOpen') && (
            <Controller
              name="additionalDollarsHeldUp"
              control={control}
              disabled={loading}
              defaultValue=""
              render={({ field: { value, onChange, ...field } }) => (
                <TextInput
                  data-testid="additionalDollarsHeldUp"
                  error={errors.additionalDollarsHeldUp && errors.additionalDollarsHeldUp.message}
                  label="Additional Dollars Held Up"
                  id="additionalDollarsHeldUp"
                  placeholder=""
                  fullWidth
                  currency
                  prefix="$"
                  className="text-base"
                  {...field}
                  value={value}
                  onChange={(e) => {
                    const updatedValue = verifyCurrencyInputValue(e, setError, clearErrors)

                    // Update the form state with the raw value without moving the cursor
                    if (updatedValue) onChange(updatedValue)
                  }}
                />
              )}
            />
          )}

          {/* Age Out Override */}
          <Controller
            name="isAgesOutOverridden"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Toggle label="Requires Age Out Override" checked={value} onChange={onChange} />
            )}
          />

          {renderRepairOrderDateField()}
        </form>
      }
      actions={[
        {
          type: 'submit',
          label: 'Save',
          onClick: handleSubmit(onSubmit),
        },
        { type: 'cancel', label: 'Cancel', onClick: () => closeModal() },
        // The destructive footer slot: intake dealers return the RO to intake (audit
        // preserved, re-addable); everyone else deletes it.
        canReturnToIntake
          ? {
              type: 'delete',
              label: 'Return to Intake',
              onClick: () => onReturnToIntake(),
            }
          : {
              type: 'delete',
              label: 'Delete RO',
              onClick: () => onDelete(),
              disabled: !isDeleteEnabled,
            },
      ]}
    />
  )
}

export default RepairOrderModal

RepairOrderModal.defaultValues = {
  defaultTechnician: '',
}

RepairOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  getUpdatedRepairOrder: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  repairOrder: PropTypes.shape({
    roNumber: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    advisor: PropTypes.string.isRequired,
    timeClockTriggerDate: PropTypes.string,
    isAgesOutOverridden: PropTypes.bool,
    isOpen: PropTypes.bool,
    isDateAddedOverridden: PropTypes.bool,
    agesOut: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  dealer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isRoIntakeEnabled: PropTypes.bool,
    manufacturer: PropTypes.shape({
      timeClockTrigger: PropTypes.string,
    }).isRequired,
  }).isRequired,
  allAdvisors: PropTypes.arrayOf(PropTypes.string).isRequired,
  setIsDeleteModalOpen: PropTypes.func.isRequired,
  setIsReturnToIntakeModalOpen: PropTypes.func.isRequired,
}
